// Vetro → as-built import wizard. Multi-step modal.
//
// Step 1: Pick a Vetro project (from /api/as-built/sources/vetro/projects)
// Step 2: Pick a plan within that project (from /sources/vetro/plans?project_id=N)
// Step 3: Parse + reconcile (POST /imports → returns diff_summary)
// Step 4: Operator reviews diff summary → Commit OR Cancel
//
// Lives in components/ so it can be reused by future as-built flows.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type VetroProject = { external_id: number | string; label: string };
type VetroPlan = {
  external_id: number | string;
  label: string;
  project_external_id: number | string;
  last_edited: string | null;
};
type ImportResp = {
  id: string;
  status: string;
  diff_summary?: any;
  parse_summary?: any;
};

type Step = 'project' | 'plan' | 'parsing' | 'review' | 'committing' | 'done' | 'error' | 'multi-running' | 'multi-done';

type MultiPlanResult = {
  planId: number | string;
  planLabel: string;
  status: 'pending' | 'parsing' | 'committing' | 'done' | 'failed';
  applied?: { created: number; updated: number; deleted: number; unchanged: number };
  error?: string;
};

export default function ImportWizardModal({
  projectId, projectLabel, onClose,
}: {
  projectId: string;
  projectLabel: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('project');
  const [vetroProjectId, setVetroProjectId] = useState<number | string | null>(null);
  const [vetroPlanId, setVetroPlanId] = useState<number | string | null>(null);
  const [vetroPlanLabel, setVetroPlanLabel] = useState<string>('');
  const [importId, setImportId] = useState<string | null>(null);
  const [diffSummary, setDiffSummary] = useState<any>(null);
  const [parseSummary, setParseSummary] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Multi-select state — checkboxes on the plan picker step
  const [multiSelect, setMultiSelect] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState<Set<string>>(new Set());
  const [multiResults, setMultiResults] = useState<MultiPlanResult[]>([]);

  // Step 1: load Vetro projects
  const { data: vetroProjData, isLoading: projLoading, error: projError } = useQuery<{ projects: VetroProject[] }>({
    queryKey: ['vetro-projects'],
    queryFn: () => api.get('/as-built/sources/vetro/projects'),
    enabled: step === 'project',
  });

  // Step 2: load plans for selected Vetro project
  const { data: vetroPlanData, isLoading: planLoading } = useQuery<{ plans: VetroPlan[] }>({
    queryKey: ['vetro-plans', vetroProjectId],
    queryFn: () => api.get(`/as-built/sources/vetro/plans?project_id=${vetroProjectId}`),
    enabled: step === 'plan' && vetroProjectId !== null,
  });

  // Step 3: POST /imports
  const importMut = useMutation({
    mutationFn: () => api.post('/as-built/imports', {
      project_id: projectId,
      source_format: 'vetro',
      source_plan_id: Number(vetroPlanId),
      source_project_label: vetroPlanLabel,
    }) as Promise<ImportResp>,
    onSuccess: (resp: ImportResp) => {
      setImportId(resp.id);
      setDiffSummary(resp.diff_summary);
      setParseSummary(resp.parse_summary);
      setStep('review');
    },
    onError: (e: any) => {
      setErrorMsg(e?.response?.data?.message ?? e?.message ?? 'Parse + reconcile failed');
      setStep('error');
    },
  });

  // Step 4: POST /imports/:id/commit
  const commitMut = useMutation({
    mutationFn: () => api.post(`/as-built/imports/${importId}/commit`, {}),
    onSuccess: () => {
      // Invalidate the page's data so KPIs + counts + import list refresh
      qc.invalidateQueries({ queryKey: ['asbuilt-stats'] });
      qc.invalidateQueries({ queryKey: ['asbuilt-imports'] });
      setStep('done');
    },
    onError: (e: any) => {
      setErrorMsg(e?.response?.data?.message ?? e?.message ?? 'Commit failed');
      setStep('error');
    },
  });

  const cancelMut = useMutation({
    mutationFn: () => importId ? api.post(`/as-built/imports/${importId}/cancel`, {}) : Promise.resolve(),
    onSuccess: () => onClose(),
  });

  const filteredPlans = (vetroPlanData?.plans ?? []).filter(p => p.project_external_id == vetroProjectId);

  // Sequential multi-plan import runner. `targets` overrides which plans to
  // run (used by the "Retry failed" button).
  async function runMultiImport(targets?: Array<{ external_id: number | string; label: string; project_external_id?: number | string }>) {
    const plansToImport: Array<{ external_id: number | string; label: string; project_external_id?: number | string }> =
      targets ?? filteredPlans.filter(p => selectedPlans.has(String(p.external_id)));
    const initial: MultiPlanResult[] = plansToImport.map(p => ({
      planId: p.external_id, planLabel: p.label, status: 'pending',
    }));
    setMultiResults(initial);
    setStep('multi-running');

    // Run each one sequentially. Each plan goes through: POST /imports (parse+reconcile)
    // → POST /imports/:id/commit. On error: mark failed, continue with remaining.
    let working = initial.slice();
    for (let i = 0; i < plansToImport.length; i++) {
      const p = plansToImport[i];
      // mark parsing
      working = working.map((r, idx) => idx === i ? { ...r, status: 'parsing' } : r);
      setMultiResults([...working]);
      try {
        // Find the parent Vetro project for grouping in the UI
        const parentProject = vetroProjData?.projects.find(
          pr => pr.external_id == (p.project_external_id ?? vetroProjectId)
        );
        const importResp = await api.post('/as-built/imports', {
          project_id: projectId,
          source_format: 'vetro',
          source_plan_id: Number(p.external_id),
          source_project_label: p.label,            // plan label (historical naming)
          source_external_project_id: Number(p.project_external_id ?? vetroProjectId) || undefined,
          source_external_project_label: parentProject?.label,
        }) as ImportResp;
        // mark committing
        working = working.map((r, idx) => idx === i ? { ...r, status: 'committing' } : r);
        setMultiResults([...working]);
        const commitResp = await api.post(`/as-built/imports/${importResp.id}/commit`, {}) as any;
        // done
        working = working.map((r, idx) => idx === i ? {
          ...r, status: 'done', applied: commitResp?.applied,
        } : r);
        setMultiResults([...working]);
      } catch (e: any) {
        const msg = e?.response?.data?.message ?? e?.message ?? String(e);
        working = working.map((r, idx) => idx === i ? { ...r, status: 'failed', error: msg } : r);
        setMultiResults([...working]);
        // If this looks like a rate-limit, wait a bit before the next plan
        if (/429|rate|frequency/i.test(msg)) {
          await new Promise(r => setTimeout(r, 3000));
        }
      }
    }

    // All done — invalidate page state + transition to summary
    qc.invalidateQueries({ queryKey: ['asbuilt-stats'] });
    qc.invalidateQueries({ queryKey: ['asbuilt-imports'] });
    qc.invalidateQueries({ queryKey: ['asbuilt-geojson'] });
    setStep('multi-done');
  }

  // Retry just the failed ones from the previous run
  async function retryFailed() {
    const failedTargets = multiResults
      .filter(r => r.status === 'failed')
      .map(r => ({ external_id: r.planId, label: r.planLabel }));
    if (failedTargets.length === 0) return;
    await runMultiImport(failedTargets);
  }

  return (
    <div
      onClick={() => step !== 'parsing' && step !== 'committing' && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 60 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 700, maxWidth: '95%', background: '#fff', borderRadius: 8, boxShadow: '0 12px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ padding: '14px 20px', background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: '#fff' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Import from Vetro · {projectLabel}</h2>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
            Pulls a Vetro plan's features → reconciles against as-built → you review → commit.
          </div>
        </div>

        {/* Stepper */}
        <Stepper current={step} />

        {/* Body */}
        <div style={{ padding: 20, minHeight: 320 }}>
          {step === 'project' && (
            <div>
              <SectionTitle>1. Select a Vetro project</SectionTitle>
              {projLoading && <LoadingState text="Loading Vetro projects…" />}
              {projError && <ErrorState text={(projError as Error).message} />}
              {vetroProjData && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
                  {vetroProjData.projects.map(p => (
                    <button
                      key={String(p.external_id)}
                      onClick={() => { setVetroProjectId(p.external_id); setStep('plan'); }}
                      style={{
                        textAlign: 'left', padding: 12,
                        border: '1px solid #cbd5e1', borderRadius: 4,
                        background: '#fff', cursor: 'pointer', fontSize: 13,
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{p.label}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, fontFamily: 'monospace' }}>
                        Vetro project_id={String(p.external_id)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'plan' && (
            <div>
              <SectionTitle>2. Select plans to import</SectionTitle>
              <div style={{ marginBottom: 10, fontSize: 12, color: '#475569' }}>
                Vetro project: <b>{vetroProjData?.projects.find(p => p.external_id == vetroProjectId)?.label}</b>
                <button onClick={() => { setVetroProjectId(null); setStep('project'); setSelectedPlans(new Set()); }} style={{ marginLeft: 10, fontSize: 11, padding: '2px 8px', border: '1px solid #cbd5e1', borderRadius: 3, background: '#fff', cursor: 'pointer' }}>← Change</button>
                {filteredPlans.length > 0 && (
                  <span style={{ marginLeft: 12, fontSize: 11 }}>
                    <button
                      onClick={() => {
                        if (selectedPlans.size === filteredPlans.length) setSelectedPlans(new Set());
                        else setSelectedPlans(new Set(filteredPlans.map(p => String(p.external_id))));
                      }}
                      style={{ padding: '2px 8px', fontSize: 11, border: '1px solid #cbd5e1', borderRadius: 3, background: '#fff', cursor: 'pointer' }}
                    >
                      {selectedPlans.size === filteredPlans.length ? 'Deselect all' : 'Select all'}
                    </button>
                    <span style={{ marginLeft: 8, color: '#64748b' }}>
                      {selectedPlans.size} of {filteredPlans.length} selected
                    </span>
                  </span>
                )}
              </div>
              {planLoading && <LoadingState text="Loading plans…" />}
              {vetroPlanData && filteredPlans.length === 0 && (
                <div style={{ padding: 16, textAlign: 'center', color: '#64748b', fontStyle: 'italic' }}>
                  No plans in this Vetro project. Pick a different project.
                </div>
              )}
              {vetroPlanData && filteredPlans.length > 0 && (
                <div style={{ display: 'grid', gap: 6, maxHeight: 360, overflowY: 'auto' }}>
                  {filteredPlans.map(p => {
                    const isSelected = selectedPlans.has(String(p.external_id));
                    return (
                      <label key={String(p.external_id)} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: 10, border: `1px solid ${isSelected ? '#3b82f6' : '#cbd5e1'}`,
                        borderRadius: 4, background: isSelected ? '#eff6ff' : '#fff',
                        cursor: 'pointer', fontSize: 13,
                      }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            const next = new Set(selectedPlans);
                            if (isSelected) next.delete(String(p.external_id));
                            else next.add(String(p.external_id));
                            setSelectedPlans(next);
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{p.label}</div>
                          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, fontFamily: 'monospace' }}>
                            plan_id={String(p.external_id)}{p.last_edited && ` · last edited ${p.last_edited.slice(0, 10)}`}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {step === 'multi-running' && (
            <div>
              <SectionTitle>Importing {multiResults.length} plans…</SectionTitle>
              <div style={{ fontSize: 12, color: '#475569', marginBottom: 12 }}>
                Each plan is parsed + reconciled + committed sequentially. Plans live in their own lanes, so they coexist without overwriting each other.
              </div>
              <div style={{ display: 'grid', gap: 6, maxHeight: 360, overflowY: 'auto' }}>
                {multiResults.map(r => (
                  <div key={String(r.planId)} style={{
                    padding: 10, border: '1px solid #e2e8f0', borderRadius: 4,
                    background: r.status === 'done' ? '#f0fdf4' : r.status === 'failed' ? '#fef2f2' : '#fff',
                    fontSize: 12,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 600 }}>{r.planLabel}</span>
                        <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 8, fontFamily: 'monospace' }}>plan_id={r.planId}</span>
                      </div>
                      <div style={{ fontSize: 11 }}>
                        {r.status === 'pending' && <span style={{ color: '#94a3b8' }}>⋯ queued</span>}
                        {r.status === 'parsing' && <span style={{ color: '#f59e0b' }}>⏳ parsing &amp; reconciling…</span>}
                        {r.status === 'committing' && <span style={{ color: '#3b82f6' }}>⏳ committing…</span>}
                        {r.status === 'done' && <span style={{ color: '#10b981', fontWeight: 600 }}>✓ done</span>}
                        {r.status === 'failed' && <span style={{ color: '#ef4444', fontWeight: 600 }}>✗ failed</span>}
                      </div>
                    </div>
                    {r.applied && (
                      <div style={{ marginTop: 4, fontSize: 11, color: '#475569' }}>
                        Applied: {r.applied.created} new · {r.applied.updated} updated · {r.applied.deleted} deleted · {r.applied.unchanged} unchanged
                      </div>
                    )}
                    {r.error && (
                      <div style={{ marginTop: 4, fontSize: 11, color: '#991b1b', fontFamily: 'monospace' }}>{r.error}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'multi-done' && (
            <div>
              <SectionTitle>✓ All imports complete</SectionTitle>
              <div style={{ padding: 14, background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6, fontSize: 13, color: '#065f46', marginBottom: 14 }}>
                {multiResults.filter(r => r.status === 'done').length} of {multiResults.length} plans imported successfully.
                Each lives in its own lane on the as-built page — toggle them in the map's plan-lane panel to compare.
              </div>
              <div style={{ display: 'grid', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
                {multiResults.map(r => (
                  <div key={String(r.planId)} style={{
                    padding: 8, border: '1px solid #e2e8f0', borderRadius: 4,
                    fontSize: 12, display: 'flex', justifyContent: 'space-between',
                    background: r.status === 'done' ? '#f0fdf4' : '#fef2f2',
                  }}>
                    <div>
                      {r.status === 'done' ? '✓ ' : '✗ '}{r.planLabel}
                      {r.applied && <span style={{ marginLeft: 8, color: '#64748b' }}>{r.applied.created} new</span>}
                    </div>
                    {r.error && <span style={{ color: '#991b1b', fontFamily: 'monospace', fontSize: 10 }}>{r.error.slice(0, 60)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'parsing' && (
            <div>
              <SectionTitle>3. Parsing &amp; reconciling…</SectionTitle>
              <LoadingState text="Pulling features from Vetro and computing the diff against your as-built. This usually takes 5-15 seconds." />
            </div>
          )}

          {step === 'review' && (
            <div>
              <SectionTitle>4. Review &amp; commit</SectionTitle>
              <div style={{ background: '#f8fafc', padding: 14, borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
                <div><b>Source:</b> Vetro plan "{vetroPlanLabel}" (plan_id={String(vetroPlanId)})</div>
                {parseSummary && (
                  <div style={{ marginTop: 6, color: '#475569', fontSize: 12 }}>
                    Parsed <b>{parseSummary.parsed_count}</b> of <b>{parseSummary.feature_count}</b> features
                    {parseSummary.skipped_count > 0 && <span> · skipped {parseSummary.skipped_count} <span style={{ color: '#94a3b8' }}>({Object.keys(parseSummary.skipped_reasons ?? {}).join(', ')})</span></span>}
                  </div>
                )}
              </div>

              <DiffBreakdown summary={diffSummary} />

              {/* Soft destructive-import warning when deletes are non-trivial */}
              {(diffSummary?.deleted ?? 0) > 0 && (
                <div style={{ marginTop: 16, padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 4, fontSize: 12, color: '#991b1b' }}>
                  <b>⚠ Destructive import:</b> {diffSummary.deleted} existing rows on this project will be soft-deleted because they don't appear in the chosen Vetro plan.
                  <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.5 }}>
                    The reconcile model treats each plan as the canonical source-of-truth for the project. If you imported a DIFFERENT plan into this same project earlier and the new plan doesn't include those features, they'll be removed. Soft-delete only — they remain queryable via <code>deleted_at IS NOT NULL</code> and any anchored permits are preserved.
                    {(diffSummary?.new ?? 0) === 0 && (diffSummary?.modified ?? 0) === 0 && (
                      <div style={{ marginTop: 6, padding: 6, background: '#fff', borderRadius: 3, fontWeight: 600 }}>
                        This commit will ONLY delete — no new or modified rows. Are you sure you want to wipe the existing as-built data?
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 16, padding: 12, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, fontSize: 12, color: '#92400e' }}>
                <b>About to commit:</b> {diffSummary?.new ?? 0} new rows, {diffSummary?.modified ?? 0} updates, {diffSummary?.deleted ?? 0} soft-deletes. Every change is recorded in <code>as_built_change_event</code> for the audit binder.
              </div>
            </div>
          )}

          {step === 'committing' && (
            <div>
              <SectionTitle>5. Committing…</SectionTitle>
              <LoadingState text="Writing to PostgreSQL in a single transaction…" />
            </div>
          )}

          {step === 'done' && (
            <div>
              <SectionTitle>✓ Import complete</SectionTitle>
              <div style={{ padding: 14, background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6, fontSize: 13, color: '#065f46' }}>
                The as-built page now reflects the imported plan. Permits will auto-detect against the new geometry (Phase B-PERM, once shipped).
              </div>
              <div style={{ marginTop: 16, fontSize: 12, color: '#64748b' }}>
                Applied:
                <ul style={{ margin: '6px 0 0 20px', padding: 0 }}>
                  <li><b>{diffSummary?.new ?? 0}</b> new rows created</li>
                  <li><b>{diffSummary?.modified ?? 0}</b> existing rows updated</li>
                  <li><b>{diffSummary?.deleted ?? 0}</b> rows soft-deleted</li>
                  <li><b>{diffSummary?.unchanged ?? 0}</b> unchanged (no-op)</li>
                </ul>
              </div>
            </div>
          )}

          {step === 'error' && (
            <div>
              <SectionTitle>Something went wrong</SectionTitle>
              <ErrorState text={errorMsg ?? 'Unknown error'} />
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {step === 'project' && (
            <button onClick={onClose} style={btnSecondary}>Cancel</button>
          )}
          {step === 'plan' && (
            <>
              <button onClick={() => { setVetroProjectId(null); setStep('project'); setSelectedPlans(new Set()); }} style={btnSecondary}>← Back</button>
              <button onClick={onClose} style={btnSecondary}>Cancel</button>
              <button
                onClick={() => runMultiImport()}
                disabled={selectedPlans.size === 0}
                style={selectedPlans.size === 0
                  ? { ...btnPrimary, opacity: 0.4, cursor: 'not-allowed' }
                  : btnPrimary}
              >
                Import {selectedPlans.size} {selectedPlans.size === 1 ? 'plan' : 'plans'} →
              </button>
            </>
          )}
          {step === 'multi-running' && (
            <span style={{ fontSize: 11, color: '#64748b', alignSelf: 'center' }}>
              Don't close this window — imports are running…
            </span>
          )}
          {step === 'multi-done' && (
            <>
              {multiResults.some(r => r.status === 'failed') && (
                <button onClick={() => retryFailed()} style={btnSecondary}>
                  ↻ Retry {multiResults.filter(r => r.status === 'failed').length} failed
                </button>
              )}
              <button onClick={onClose} style={btnPrimary}>Close</button>
            </>
          )}
          {step === 'review' && (
            <>
              <button onClick={() => cancelMut.mutate()} disabled={cancelMut.isPending} style={btnSecondary}>
                {cancelMut.isPending ? 'Cancelling…' : 'Discard'}
              </button>
              <button
                onClick={() => { setStep('committing'); commitMut.mutate(); }}
                disabled={commitMut.isPending}
                style={btnPrimary}
              >
                Commit import
              </button>
            </>
          )}
          {(step === 'done' || step === 'error') && (
            <button onClick={onClose} style={btnPrimary}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Stepper({ current }: { current: Step }) {
  const steps: { key: Step; label: string }[] = [
    { key: 'project', label: 'Project' },
    { key: 'plan',    label: 'Plan' },
    { key: 'parsing', label: 'Parse & reconcile' },
    { key: 'review',  label: 'Review' },
    { key: 'done',    label: 'Done' },
  ];
  const indexOf = (s: Step) => {
    if (s === 'parsing')    return 2;
    if (s === 'review')     return 3;
    if (s === 'committing') return 3;
    if (s === 'done')       return 4;
    if (s === 'error')      return -1;
    return steps.findIndex(x => x.key === s);
  };
  const cur = indexOf(current);
  return (
    <div style={{ display: 'flex', gap: 4, padding: '10px 20px', background: '#f1f5f9', borderBottom: '1px solid var(--border)' }}>
      {steps.map((s, i) => (
        <div key={s.key} style={{
          flex: 1, padding: '4px 8px', fontSize: 11, textAlign: 'center',
          background: i < cur ? '#bbf7d0' : i === cur ? '#dbeafe' : '#fff',
          color: i < cur ? '#065f46' : i === cur ? '#1e40af' : '#94a3b8',
          border: '1px solid', borderColor: i < cur ? '#86efac' : i === cur ? '#93c5fd' : '#e2e8f0',
          borderRadius: 4, fontWeight: 600,
        }}>
          {i < cur ? '✓ ' : ''}{s.label}
        </div>
      ))}
    </div>
  );
}

function SectionTitle({ children }: { children: any }) {
  return <h3 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{children}</h3>;
}

function LoadingState({ text }: { text: string }) {
  return (
    <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>
      <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
      <div style={{ fontSize: 13 }}>{text}</div>
    </div>
  );
}

function ErrorState({ text }: { text: string }) {
  return (
    <div style={{ padding: 14, background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 4, fontSize: 12 }}>
      {text}
    </div>
  );
}

function DiffBreakdown({ summary }: { summary: any }) {
  if (!summary) return null;
  const tables = ['segments', 'poles', 'vaults', 'closures', 'drops', 'equipment', 'structures', 'hardware', 'ducts'];
  const totalRows = (summary.unchanged ?? 0) + (summary.modified ?? 0) + (summary.deleted ?? 0) + (summary.new ?? 0);
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
        <StatPill label="Unchanged" value={summary.unchanged ?? 0} color="#64748b" />
        <StatPill label="Modified"  value={summary.modified ?? 0}  color="#f59e0b" />
        <StatPill label="Deleted"   value={summary.deleted ?? 0}   color="#ef4444" />
        <StatPill label="New"       value={summary.new ?? 0}       color="#10b981" />
      </div>
      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f1f5f9' }}>
            <th style={{ textAlign: 'left',  padding: '6px 10px' }}>By kind</th>
            <th style={{ textAlign: 'right', padding: '6px 10px', color: '#64748b' }}>Unchanged</th>
            <th style={{ textAlign: 'right', padding: '6px 10px', color: '#f59e0b' }}>Modified</th>
            <th style={{ textAlign: 'right', padding: '6px 10px', color: '#ef4444' }}>Deleted</th>
            <th style={{ textAlign: 'right', padding: '6px 10px', color: '#10b981' }}>New</th>
          </tr>
        </thead>
        <tbody>
          {tables.map(t => {
            const r = summary.by_table?.[t];
            if (!r) return null;
            const tot = (r.unchanged ?? 0) + (r.modified ?? 0) + (r.deleted ?? 0) + (r.new ?? 0);
            if (tot === 0) return null;
            return (
              <tr key={t} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '6px 10px', fontWeight: 600 }}>{t}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: r.unchanged > 0 ? '#0f172a' : '#cbd5e1' }}>{r.unchanged}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: r.modified > 0 ? '#b45309' : '#cbd5e1' }}>{r.modified}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: r.deleted > 0 ? '#b91c1c' : '#cbd5e1' }}>{r.deleted}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: r.new > 0 ? '#047857' : '#cbd5e1', fontWeight: r.new > 0 ? 700 : 400 }}>{r.new}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>Total rows in diff: {totalRows}</div>
    </div>
  );
}

function StatPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ padding: 10, border: '1px solid var(--border)', borderRadius: 6, background: '#fff', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2, color }}>{value}</div>
    </div>
  );
}

const btnSecondary = {
  padding: '7px 14px', fontSize: 12,
  border: '1px solid #cbd5e1', borderRadius: 4,
  background: '#fff', color: '#0f172a', cursor: 'pointer',
} as const;

const btnPrimary = {
  padding: '7px 16px', fontSize: 12, fontWeight: 600,
  border: 0, borderRadius: 4,
  background: '#1e3a8a', color: '#fff', cursor: 'pointer',
} as const;
